import React, { useState } from 'react';
import { Box, Typography } from '@mui/material';
import { useForm } from 'react-hook-form';
import { BackrsForm } from '../../molecules/BackrsForm/BackrsForm';
import { TextWithEmbeddedButton } from '../TextWithEmbeddedButton';
import appStrings from '../../app_strings';
import appColors from '../../app_colors';
import { appBorders } from '../../app_borders';
import { ForgotPassword } from '../../molecules/ForgotPassword';

const LOGIN_STRINGS = appStrings.login.unified;

const containerStyle = {
  height: '100vh',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  px: 4,
  backgroundColor: appColors.primaryAppBackground,
};

const loginFormStyle = {
  backgroundColor: appColors.container.white,
  paddingX: 2,
  paddingY: 3,
  borderRadius: 4,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  maxWidth: '100%',
  width: '100%',
  boxSizing: 'border-box',
  '@media (min-width: 300px)': {
    paddingX: 2,
    paddingY: 4,
  },
  '@media (min-width: 600px)': {
    maxWidth: '500px',
    paddingX: 11,
    paddingY: 8,
  },
  ...appBorders.backrsDropShadowBorders.black,
};

const responsiveBackrsLogoStyle = {
  width: '100%',
  maxWidth: '175px',
  padding: 1,
  mb: 4,
};

const defaultLoginValues = {
  phone: '',
  email: '',
  password: '',
};

const LoginPage = ({
  title,
  handleLoggingIn,
  handleSigningUp,
  errorMessage,
}) => {
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const methods = useForm({
    defaultValues: defaultLoginValues,
    resolver: (values) => {
      const errors = {};

      if (!values.phone && !values.email) {
        errors.phone = 'Phone number or email is required';
      }

      if (values.phone && !/^\d{10}$/.test(values.phone)) {
        errors.phone = 'Please enter a valid phone number';
      }

      if (values.email && !/^\S+@\S+$/.test(values.email)) {
        errors.email = 'Please enter a valid email';
      }

      if (values.email && !values.password && !values.phone) {
        errors.password = 'Password is required';
      }
      return { values, errors };
    },
  });
  const phoneValue = methods.watch('phone');
  const emailValue = methods.watch('email');
  const passwordValue = methods.watch('password');

  const fields = [
    {
      id: 'phone',
      name: 'phone',
      placeholder: 'Mobile number',
      type: 'tel',
      autoComplete: 'phone',
      icon: {
        type: 'LEFT_ICON',
        leftIcon: 'Phone',
        leftIconColor: appColors.buttons.secondaryBackground,
        enableFill: true,
      },
      disabled: !!emailValue && !!passwordValue,
    },
    {
      id: 'email',
      name: 'email',
      placeholder: 'Email',
      type: 'text',
      autoComplete: 'email',
      icon: {
        type: 'LEFT_ICON',
        leftIcon: 'Email',
        leftIconColor: appColors.buttons.secondaryBackground,
      },
      disabled: !!phoneValue,
      divider: true,
    },
    {
      id: 'password',
      name: 'password',
      placeholder: 'Password',
      type: isPasswordVisible ? 'text' : 'password',
      autoComplete: 'current-password',
      icon: {
        type: 'BOTH_ICON',
        leftIcon: 'Lock',
        leftIconColor: appColors.buttons.secondaryBackground,
        rightIcon: 'Visibility',
        rightIconColor: appColors.icon.taupe,
        onRightIconClick: () => setIsPasswordVisible((prev) => !prev),
      },
      disabled: !!phoneValue,
    },
  ];

  const handleSubmission = (data) => {
    handleLoggingIn(data.phone, data.email, data.password);
  };

  return (
    <Box sx={containerStyle}>
      <Box
        component="img"
        src="/assets/backrs-with-text.svg"
        alt="backrs logo with text"
        sx={responsiveBackrsLogoStyle}
      />
      <Box sx={loginFormStyle}>
        <Typography variant="h4" fontWeight="bold" color={appColors.text.darkNavy} mb={3}>
          {title}
        </Typography>
        <TextWithEmbeddedButton
          text={LOGIN_STRINGS.getStartedPrompt}
          buttonText={LOGIN_STRINGS.getStartedButton}
          onClick={handleSigningUp}
          sx={{ mb: 8 }}
        />
        <BackrsForm
          fields={fields}
          onSubmit={handleSubmission}
          buttonLabel={LOGIN_STRINGS.letsGo}
          methods={methods}
        >
          <ForgotPassword />
        </BackrsForm>
        {errorMessage && (
          <Box mt={2} color={appColors.text.red}>
            {errorMessage}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default LoginPage;
